import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import dayjs from 'dayjs'
import calendar from 'dayjs/plugin/calendar'
import { message, DatePicker, Select, Tooltip, Table } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faThumbsUp, faThumbsDown } from '@fortawesome/free-regular-svg-icons'
import { FaChevronDown, FaChevronLeft, FaChevronRight, FaChevronUp, FaCopy } from 'react-icons/fa6'
import { FaCheckCircle } from 'react-icons/fa'

import './styles.scss'
import ESGLogoIcon from '../../../assets/images/ESG-Chat-logo-icon.svg'
import ESGUserIcon from '../../../assets/images/ESG-Chat-User-Icon.svg'
import ESGSourceIcon from '../../../assets/images/ESG-Chat-Source-Icon.svg'
import NoHistoryData from '../../../assets/images/ESG-Chat-No-Data.svg'
import SpinLoader from '../../../assets/component/loader/loader'
import { ESGChatHistoryAction, EsgChatHistoryReset } from '../../../store/slices/ai-chat/history'
import { ChatResponseRef, tableHeaders } from '../esg-chat/chart-card'
import { formatChatReference, formatChunkReference, searchByOptions } from '../esg-chat'
import Breadcrumbs from '../../../assets/component/breadcrumbs'
import PDFPreview from '../../../assets/component/pdf-preview'
import { decryptData } from '../../../helpers/encrypt/crypto-encrypt'

dayjs.extend(calendar)

const feedbackData = [
    {
        id: 1,
        label: 'Answer Found',
        value: 'answer found',
        icon: faThumbsUp,
        className: 'answer-found',
    },
    {
        id: 2,
        label: 'Answer Not Found',
        value: 'answer not found',
        icon: faThumbsDown,
        className: 'answer-not-found',
    },
]

const ClearChatPopup = ({ setShowClearChatPopup, confirmDelete }) => {
    return (
        <div className="clear-chat-popup-container">
            <div className="text-container">Are you sure?</div>
            <div className="description-container">Your chat history will be permanently deleted.</div>
            <div className="buttons-section">
                <div className="popup-button confirm-button" onClick={() => confirmDelete()}>
                    Confirm
                </div>
                <div className="popup-button cancel-button" onClick={() => setShowClearChatPopup(false)}>
                    Cancel
                </div>
            </div>
        </div>
    )
}

const HistoryChatCard = ({ data, chatIndex, cardRef, handlePDFPreview, model, tableHeaders, setGoToPage, promptEvaluationFlag, index }) => {
    
    const [copyQuestion, setCopyQuestion] = useState(false)
    const [copyAnswer, setCopyAnswer] = useState(false)
    const [expandSource, setExpandSource] = useState(false)
    const [expandResponse, setExpandResponse] = useState(false)

    const currentFeedback = feedbackData?.find((feedOption) => feedOption?.value === data?.feedback)

    const handleCopyQuestion = (question) => {
        setCopyQuestion(true)
        navigator.clipboard.writeText(question)

        setTimeout(() => {
            setCopyQuestion(false)
        }, 2000)
    }

    const handleCopyAnswer = () => {
        setCopyAnswer(true)
        const answerText = document.getElementById(`history-answer-text-${chatIndex}`)?.innerText
        navigator.clipboard.writeText(answerText)

        setTimeout(() => {
            setCopyAnswer(false)
        }, 2000)
    }

    const handleCollapse = () => {
        setExpandSource(!expandSource)
    }

    const handleResponseCollapse = () => {
        setExpandResponse(!expandResponse)
    }

    useEffect(() => {
        const container = document.getElementById(`history-answer-text-${chatIndex}`);
        if (container) {
            const links = container.querySelectorAll("a");
            links.forEach((link) => {
                link.setAttribute("target", "_blank");
                link.setAttribute("rel", "noopener noreferrer")
            })
        }
    }, [expandResponse])

    return (
        <div className="chat-card" ref={cardRef}>
            <div className="chat-card-section time-section">
                <div className="date-time">{data?.createdAt}</div>
            </div>
            <div className="chat-card-section question-section">
                <div className="left-section">
                    <img className="icons esg-user" src={ESGUserIcon} alt="ESG icon" />
                </div>
                <div className="right-section">
                    <div className="question-container">
                        {data?.question}
                        <span className="regenerate-question" onClick={() => handleCopyQuestion(data?.question)}>
                            <Tooltip
                                title={
                                    !copyQuestion ? (
                                        'Copy'
                                    ) : (
                                        <div className="copied-section">
                                            <FaCheckCircle style={{ marginBottom: '-2px' }} /> Copied
                                        </div>
                                    )
                                }
                            >
                                <FaCopy />
                            </Tooltip>
                        </span>
                    </div>
                </div>
            </div>
            <div className="chat-card-section payload-filters-section">
                <div className="right-section">
                    {data?.filters?.map((filter) => {
                        if (filter?.value) {
                            return (
                                <div className="filter-item" key={filter?.title}>
                                    <span className="filter-title">{filter?.title} :</span>
                                    <span className="filter-value">{filter?.value}</span>
                                </div>
                            )
                        }
                    })}
                </div>
            </div>
            {!data?.requestType && <div className="chat-card-section section-title">
                <div className="left-section">
                    <img className="icons esg-logo" src={ESGLogoIcon} alt="ESG user icon" />
                </div>
                <div className="right-section">
                    <div className="answer-section">
                        <div className="answer-section-title">
                            Response{' '}
                            <Tooltip
                                title={
                                    !copyAnswer ? (
                                        'Copy Answer'
                                    ) : (
                                        <div className="copied-section">
                                            <FaCheckCircle style={{ marginBottom: '-2px' }} /> Answer Copied
                                        </div>
                                    )
                                }
                            >
                                <FaCopy onClick={() => handleCopyAnswer()} />
                            </Tooltip>
                        </div>
                        <div className="expand-collapse-text" onClick={() => handleResponseCollapse()}>
                            {!expandResponse ? (
                                <div className="expand-collapse-button">
                                    Show <FaChevronDown />
                                </div>
                            ) : (
                                <div className="expand-collapse-button">
                                    Hide <FaChevronUp />
                                </div>
                            )}
                        </div>
                    </div>
                    {data?.feedback && currentFeedback?.icon ? (
                        <div className={`feedback-icon ${currentFeedback?.className}`}>
                            <FontAwesomeIcon icon={currentFeedback?.icon} />
                        </div>
                    ) : (
                        <div className="no-feedback">-</div>
                    )}
                </div>
            </div>}

            {expandResponse && (
                <div className="chat-card-section answer-section">
                    <div className="right-section">
                        {data?.answer?.response ? (
                            <div className="answer-text" id={`history-answer-text-${chatIndex}`}>
                                {/* <Table
                                    dataSource={data?.answer ? [data?.answer] : []}
                                    columns={tableHeaders}
                                    pagination={false}
                                /> */}
                                <div className="key-value-container">
                                    {(() => {
                                        const headers = tableHeaders; // already passed as prop
                                        const headerMap = Object?.fromEntries(
                                            headers?.map(h => [h.dataIndex, h.title])
                                        );

                                        return Object.entries(data?.answer || {})
                                            .filter(([key, value]) => value !== undefined && value !== null)
                                            .map(([key, value]) => {
                                                // ⭐ Handle merged Source Details
                                                if (key === "sourceDetails" && typeof value === "object") {
                                                    const { sourceType, sourceTitle, sourceUrl } = value;

                                                    return (
                                                        <div className="key-value-row" key={key}>
                                                            <span className="kv-key">Source Details</span>

                                                            <span className="kv-value">
                                                                {[
                                                                    sourceType || "NA",
                                                                    sourceTitle || "NA",
                                                                    sourceUrl?.trim() && sourceUrl !== "NA"
                                                                        ? <a
                                                                            href={sourceUrl}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            style={{ color: "#1890ff", textDecoration: "underline" }}
                                                                            key="sourceLink"
                                                                        >
                                                                            Open Link
                                                                        </a>
                                                                        : "No URL"
                                                                ].map((item, index) =>
                                                                    <span key={index}>
                                                                        {index > 0 && " / "}
                                                                        {item}
                                                                    </span>
                                                                )}
                                                            </span>

                                                        </div>
                                                    );
                                                }
                                                if (["sourceTitle", "sourceUrl", "sourceType"]?.includes(key)) {
                                                    return null;
                                                }

                                                return (
                                                    <div className="key-value-row" key={key}>
                                                        <span className="kv-key">{headerMap[key] || key.replace(/_/g, " ")}</span>
                                                        <span className="kv-value">
                                                            {typeof value === "object" ? JSON.stringify(value) : value?.toString()}
                                                        </span>
                                                    </div>
                                                );
                                            });

                                    })()}
                                </div>

                            </div>
                        ) : (
                            <div
                                className="answer-text"
                                id={`history-answer-text-${chatIndex}`}
                                dangerouslySetInnerHTML={{
                                    __html: data?.answer || 'Answer not found in the document!',
                                }}
                            />
                        )}
                    </div>
                </div>
            )}

            {data?.sources?.length > 0 && (
                <div className="chat-card-section section-title">
                    <div className="left-section">
                        <img className="icons source-icon" src={ESGSourceIcon} alt="ESG source icon" />
                    </div>
                    <div className="right-section">
                        <div className="source-section-contents">
                            <div className="right-section-title">References</div>
                            <span className="references-length">{`(${data?.sources?.length})`}</span>
                            {data?.sources?.length > 0 && (
                                <div className="expand-collapse-text" onClick={() => handleCollapse()}>
                                    {!expandSource ? (
                                        <div className="expand-collapse-button">
                                            Show <FaChevronDown />
                                        </div>
                                    ) : (
                                        <div className="expand-collapse-button">
                                            Hide <FaChevronUp />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {expandSource && (
                <div className="chat-card-section reference-section">
                    <div className="right-section">
                        {data?.sources?.map((desc, i) => {
                            return <ChatResponseRef data={desc} key={i} handlePDFPreview={handlePDFPreview} showUploadSection={model === 'Custom'} setGoToPage={setGoToPage} promptEvaluationFlag={promptEvaluationFlag} chunkEvaluation={data?.requestType} index={i+1}/>
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}

const ChatHistoryMain = ({ model, searchBy, promptEvaluation }) => {
    const [showFiltersSection, setShowFiltersSection] = useState(true)
    const [showClearChatPopup, setShowClearChatPopup] = useState(false)
    const [dateRange, setDateRange] = useState(null);
    const [feedback, setFeedback] = useState([]);
    const [chatData, setChatData] = useState([])
    const [pageCount, setPageCount] = useState(1)
    const [isScrolledToTop, setIsScrolledToTop] = useState(false)
    const [allowHistoryFetch, setAllowHistoryFetch] = useState(false)
    const [showPDFPreview, setShowPDFPreview] = useState({ show: false, isPdf: true })
    const [currentPageIndex, setCurrentPageIndex] = useState(0);

    const dispatch = useDispatch()
    const scrollRef = useRef()
    const cardRefs = useRef([])
    const { RangePicker } = DatePicker
    const role_decrypt = decryptData(sessionStorage.getItem('eqlwoer'))
    const currentUserRole = role_decrypt?.role
    const { data: chatHistoryData, error: chatHistoryError, isLoading: chatHistoryLoading } = useSelector((state) => state.chatHistory)
    
    const handlePDFPreview = ({ url }) => {
        setShowPDFPreview({ show: true, isPdf: url?.includes('.pdf') })
    }

    const scrollToBottom = () => {
        if (scrollRef.current) {
            setTimeout(() => {
                scrollRef.current.scrollTop = scrollRef.current.scrollHeight
                scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }, 100)
        }
    }

    const scrollToCardFromBottom = (indexFromBottom) => {
        const card = cardRefs.current[indexFromBottom - 2]
        if (card && scrollRef.current) {
            scrollRef.current.scrollTo({
                top: card.offsetTop,
                behavior: 'smooth', // Smooth scrolling
            })
        }
    }

    const retriveHistory = ({ clear, pageNumber, applyFilter }) => {
        setPageCount(pageNumber)

        let payload = {
            page: pageNumber,
            limit: 10,
            clear: clear,
            feedback: applyFilter ? (feedback?.length > 0 ? feedback : '') : '',
            searchType: model,
            searchBy: searchBy,
            isLabs: promptEvaluation,
            createdAt: applyFilter ? dateRange?.date : "",
        }

        dispatch(ESGChatHistoryAction(payload))
        setAllowHistoryFetch(false)
    }

    const handleFiltersSection = () => {
        setShowFiltersSection(!showFiltersSection)
    }

    const onChangeDate = (e) => {
        let fromDate = e ? dayjs(e[0]).format('DD-MMM-YYYY') : ''
        let toDate = e ? dayjs(e[1]).format('DD-MMM-YYYY') : ''
        setDateRange({ date: { startDate: fromDate, endDate: toDate }, value: e })
    }

    const handleChangeFeedback = (e) => {
        setFeedback(e)
    }

    const handleApplyFilter = () => {
        setChatData([]);
        setAllowHistoryFetch(false);
        retriveHistory({ clear: false, pageNumber: 1, applyFilter: true });
    };

    const handleClearFilter = () => {
        setDateRange(null);
        setFeedback([]);
        setChatData([]);
        setAllowHistoryFetch(false);
        retriveHistory({ clear: false, pageNumber: 1, applyFilter: false });
    };

    const handleConfirmDelete = () => {
        retriveHistory({ clear: true, applyFilter: false });
    };


    const handleClearChatHistory = () => {
        if (chatData?.length > 0) {
            setShowClearChatPopup(true)
        }
    }

    const formatCreatedDate = (createdDate) => {
        const date = dayjs(createdDate)
        const now = dayjs()
        if (date.isSame(now, 'day') || date.isSame(now.subtract(1, 'day'), 'day')) {
            return date.calendar(now).replace('at ', '')
        } else {
            return date.format('DD-MMM-YYYY hh:mm A')
        }
    }

    // Format Chat History
    const formatChatHistory = (data) => {
        const folderValue = data?.payload?.customFolder

        const currentReferences = data?.references
            ?.map((item) => {
                const listData = item?.retrievedReferences?.map((data) => {
                    return formatChatReference({ data: data, searchType: model })
                })
                return listData
            })
            .flat()

        const chunkReference = data?.payload?.requestType ? data?.references?.map((item) => {
            return formatChunkReference(item)
        }).flat() : [];

        const matchedSearchBy = searchByOptions?.find((item) => item?.payloadSearchType === data?.payload?.searchType)
        const isArrayResponse = Array.isArray(data?.answer?.response);
        const hasMultipleResponses = isArrayResponse && data?.answer?.response?.length > 1;
        let formattedResponse;
        if (hasMultipleResponses) {
            formattedResponse = (
                <ul>
                    {data?.answer?.response?.map((res, idx) => (
                        <li key={idx}>
                            <span>{res}</span>
                        </li>
                    ))}
                </ul>
            );
        } else if (isArrayResponse && data?.answer?.response?.length === 1) {
            formattedResponse = data?.answer?.response?.toString();
        } else {
            formattedResponse = data?.response || "-";
        }
        const formattedData = {
            createdAt: data?.createdAt ? formatCreatedDate(data?.createdAt) : '',
            question: data?.payload?.question || data?.payload?.searchQuery,
            answer: data?.answer?.response
                ? {
                    response: formattedResponse,
                    response_unit: data?.answer?.response_unit,
                    text_snippet: data?.answer?.text_snippet,
                    pageNumber: data?.answer?.pageNumber,
                    sourceDetails: {
                        sourceTitle: data?.answer?.sourceTitle,
                        sourceUrl: data?.answer?.sourceUrl,
                        sourceType: data?.answer?.sourceType,
                    },
                    justification: data?.answer?.justification,
                    confidenceScore: data?.answer?.confidenceScore,
                    borderlineCase: data?.answer?.borderlineCase ? "Yes" : "No",
                    searchQuery: data?.answer?.searchQuery || "",
                }
                : data?.answer,
            feedback: data?.userFeedback,
            requestType: data?.payload?.requestType,
            sources: data?.payload?.requestType ? chunkReference : currentReferences,
            filters: [
                { title: 'Search by', value: matchedSearchBy?.title },
                { title: 'Folder', value: folderValue },
                { title: `${data?.payload?.companyName?.length > 1 ? matchedSearchBy?.pluralText : matchedSearchBy?.title}`, value: !folderValue ? data?.payload?.companyName?.join(', ') : undefined },
                { title: `${data?.payload?.year?.length > 1 ? 'Years' : 'Year'}`, value: !folderValue ? data?.payload?.year?.length > 0 && data?.payload?.year?.join(', ') : undefined },
                { title: 'Playbook Name', value: data?.playbookName },
                {title: 'Model',value: data?.answer?.model === "haiku" ? "Basic" : data?.answer === null ? "" : "Advanced"}
            ],
        }

        return formattedData
    }

    const handleResize = () => {
        if (window.innerWidth <= 768) {
            setShowFiltersSection(false)
        }
    }

    // Initial Call
    useEffect(() => {
        dispatch(EsgChatHistoryReset())
        retriveHistory({ clear: false, pageNumber: 1, applyFilter: false })
    }, [])

    useEffect(() => {
        if (chatHistoryError?.message) {
            message?.error(chatHistoryError?.message)
        }
    }, [chatHistoryError?.message])

    useEffect(() => {
        if (showClearChatPopup && chatHistoryData?.message) {
            message.success(chatHistoryData?.message)
            retriveHistory({ clear: false, pageNumber: 1, applyFilter: false });
            setShowClearChatPopup(false)
        }
    }, [chatHistoryData?.message])

    useEffect(() => {
        const chatContainer = scrollRef.current
        const handleScroll = () => {
            if (chatContainer.scrollTop === 0) {
                setIsScrolledToTop(true)
            }
        }

        if (chatData.length > 0) {
            chatContainer.addEventListener('scroll', handleScroll)
            return () => {
                chatContainer.removeEventListener('scroll', handleScroll)
            }
        }
    }, [chatData])

    useEffect(() => {
        if (isScrolledToTop && allowHistoryFetch) {
            retriveHistory({ clear: false, pageNumber: pageCount + 1, applyFilter: true });
            setIsScrolledToTop(false);
        }
    }, [isScrolledToTop, allowHistoryFetch])

    useEffect(() => {
        const chatHistory = chatHistoryData?.chatHistory || []

        if (chatHistory?.length > 0) {
            const formattedHistoryData = chatHistory?.map((data) => formatChatHistory(data))
            const timeFormattedData = formattedHistoryData?.reverse()

            const formattedChatWithHistory = [...timeFormattedData, ...chatData]

            setChatData(formattedChatWithHistory)
            setAllowHistoryFetch(chatHistory?.length === 10)

            if (formattedChatWithHistory.length <= 10 && formattedChatWithHistory.length > 0) {
                scrollToBottom()
            } else {
                const cardPosition = chatHistory?.length || 1
                scrollToCardFromBottom(cardPosition)
            }
        } else if (chatHistory?.length === 0 && pageCount === 1) {
            setChatData([])
        }
    }, [chatHistoryData?.chatHistory])

    useEffect(() => {
        handleResize()
        window.addEventListener('resize', handleResize)
        return () => {
            window.removeEventListener('resize', handleResize) //Clean up
        }
    }, [])

    return (
        <div className="chat-history-page">
            <Breadcrumbs />
            <div className="chat-history-body">
                {/* Right Section */}
                <div className="chat-history-right-section">
                    {showFiltersSection && (
                        <div className="filters-section">
                            <div className="filters-contents">
                                <div className="filters-head">Chat Filters</div>
                                <div className="input-wrapper">
                                    <div className="label">Date Range</div>
                                    <div className="input-container">
                                        <RangePicker className="date-range-input" onChange={onChangeDate} value={dateRange?.value} format={'DD-MMM-YYYY'} size="large" />
                                    </div>
                                </div>
                                <div className="input-wrapper">
                                    <div className="label">Feedback</div>
                                    <div className="input-container">
                                        <Select
                                            className="feedback-select"
                                            popupClassName="chat-react-select"
                                            options={feedbackData || []}
                                            onChange={(e) => handleChangeFeedback(e)}
                                            value={feedback}
                                            showSearch
                                            allowClear
                                            placeholder="Select Feedback"
                                            filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
                                        />
                                    </div>
                                </div>
                            </div >
                            <div className="user-section">
                                <div className="filter-buttons-container">
                                    <div className="filter-button apply-filter" onClick={() => handleApplyFilter()}>
                                        Apply Filter
                                    </div>
                                    <div className="filter-button clear-filter" onClick={() => handleClearFilter()}>
                                        Clear Filter
                                    </div>
                                </div>
                                <div className="clear-history-button" onClick={() => handleClearChatHistory()}>
                                    Clear Chat
                                </div>
                            </div>
                            {showClearChatPopup && <ClearChatPopup setShowClearChatPopup={setShowClearChatPopup} confirmDelete={handleConfirmDelete} />}
                        </div >
                    )}

                    <div className="expand-collapse-section-container">
                        <div className="expand-collapse-button" onClick={() => handleFiltersSection()}>
                            {showFiltersSection ? <FaChevronRight /> : <FaChevronLeft />}
                        </div>
                    </div>
                </div >

                <div className="chat-history-body-main">
                    <div className="chat-history-section-container" ref={scrollRef}>
                        {chatData?.length > 0 ? (
                            <div className="chat-wrap">
                                {chatData?.map((chatItem, index) => {
                                    return (
                                        <HistoryChatCard
                                            key={index}
                                            model={model}
                                            data={chatItem}
                                            chatIndex={index}
                                            cardRef={(el) => (cardRefs.current[index] = el)}
                                            handlePDFPreview={handlePDFPreview}
                                            tableHeaders={tableHeaders("Company")}
                                            setGoToPage={setCurrentPageIndex}
                                            promptEvaluationFlag={promptEvaluation}
                                        />
                                    )
                                })}
                            </div>
                        ) : (
                            <>
                                {!chatHistoryLoading ? (
                                    <div className="no-data-error">
                                        <img className="error-image" src={NoHistoryData} alt="no-historical-data" />
                                        <div className="error-text">No historical data available!</div>
                                    </div>
                                ) : (
                                    <div className="loading-container">
                                        <SpinLoader />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div >

            {/* PDF Preview */}
            {showPDFPreview?.show && <PDFPreview setShow={setShowPDFPreview} isDocDownload={!showPDFPreview?.isPdf} goToPage={currentPageIndex} setGoToPage={setCurrentPageIndex} />}
        </div >
    )
}

export default ChatHistoryMain
